/* eslint-disable max-lines */
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { FormattedMessage, useIntl } from "react-intl";

import { validateFedexAddress } from "api";
import errorToast from "helpers/errorToast";
import { getLabelWithTranslationFallback } from "helpers/getLabel";
import { ReactComponent as FedexLogo } from "assets/images/iconsV2/fedex-seeklogo.svg";
import Select from "react-select";
import Radio from "components/v2/Radio/Radio";
import Badge from "components/v2/Badge/Badge";
import DualListField from "components/v2/DualList/DualListField";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import Input from "components/v2/Input/Input";
import Alert from "components/v2/Alert/Alert";
import Checkbox from "components/v2/Checkbox/Checkbox";
import Button from "components/v2/Button/Button";
import Switch from "components/v2/Switch/Switch";
import { useGetCountries } from "api/queries/common/useGetCountries";
import useUnsavedChangesWarning from "hooks/useUnsavedChangesWarning";
import { useCreateParcelAccountContext, AddressFormData, AddressValidationResponse } from "../CreateParcelAccountState";
import CreateParcelAccountAgreementModal from "./CreateParcelAccountAgreementModal";

function CreateParcelAccountAddressForm() {
  const navigate = useNavigate();
  const { dispatch } = useCreateParcelAccountContext();
  const intl = useIntl();
  const [isEulaModalOpen, setIsEulaModalOpen] = useState(false);

  const methods = useForm<AddressFormData>({
    mode: "onChange",
    defaultValues: {
      account_details: {
        is_active: true,
        account_name: "",
        account_number: "",
        account_type: "company",
        label_format: "pdf",
        fedex_end_user_license_agreement: false,
        available_to_locations: [],
        inv_reference_field: null,
        ref_reference_field: null,
        po_reference_field: null,
        dept_reference_field: null,
      },
      country_code: "",
      state_code: "",
      address_line_1: "",
      address_line_2: "",
      city: "",
      postal_code: "",
    },
  });

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty, isValid },
  } = methods;

  const paymentLocations: any = useSortedPaymentLocations();
  const accountType = watch("account_details.account_type");
  const isActive = watch("account_details.is_active");
  const isEulaAccepted = watch("account_details.fedex_end_user_license_agreement");

  const { data: countries = [] } = useGetCountries({
    select: (data: any) =>
      data.map(({ label, code, id }: { label: string; code: string; id: number }) => ({
        id,
        label,
        value: code,
      })),
  });
  const referenceOptions = [
    {
      id: 1,
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.wbsField",
        defaultMessage: "WBS",
      }),
      value: "wbs",
    },
    {
      id: 2,
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.shipmentId",
        defaultMessage: "Shipment ID",
      }),
      value: "shipment_id",
    },
    {
      id: 3,
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.quickRequestCreator",
        defaultMessage: "Quick Request Creator",
      }),
      value: "quick_request_creator",
    },
    {
      id: 4,
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.quoteRequestCreator",
        defaultMessage: "Quote Request Creator",
      }),
      value: "quote_request_creator",
    },
    {
      id: 5,
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.pickupContactName",
        defaultMessage: "Pickup Contact Name",
      }),
      value: "pickup_contact_name",
    },
    {
      id: 6,
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.deliveryContactName",
        defaultMessage: "Delivery Contact Name",
      }),
      value: "delivery_contact_name",
    },
    {
      id: 7,
      label: getLabelWithTranslationFallback("Custom field 01", {
        style: "capitalize",
        paymentLocation: paymentLocations,
        returnAsString: true,
        translation: (
          <FormattedMessage id="new-request.quote-confirmation.organisation.customField1" defaultMessage="Custom field 1" />
        ),
      }),
      value: "custom_field_01",
    },
  ];

  const { UnsavedChangesModal, confirmNavigation } = useUnsavedChangesWarning({
    isDirty,
    title: intl.formatMessage({
      id: "settings.parcelAccounts.createAccount.unsavedTitle",
      defaultMessage: "Discard account creation?",
    }),
    message: intl.formatMessage({
      id: "settings.parcelAccounts.createAccount.unsavedMessage",
      defaultMessage: "All of your entered information will be lost. Proceed?",
    }),
  });

  const { mutate: validateAddress, isLoading: isValidating } = useMutation({
    mutationFn: (payload: AddressFormData) => validateFedexAddress(payload),
    onSuccess: (response: AxiosResponse<AddressValidationResponse>, variables: AddressFormData) => {
      if (response.status === 201) {
        toast.success(
          intl.formatMessage({
            id: "settings.parcelAccounts.createAccount.createSuccess",
            defaultMessage: "FedEx account created successfully",
          }),
        );
        navigate("/settings/parcel-accounts", { replace: true });
        return;
      }
      dispatch({
        type: "SET_VALIDATION_SUCCESS",
        payload: {
          addressFormData: {
            ...variables,
          },
          addressValidationResponse: response.data,
        },
      });
    },
    onError: (error: any) => {
      error.response.data = error.response.data?.non_field_errors || error.response.data;
      errorToast(error);
    },
  });

  const onSubmit = (data: AddressFormData) => {
    // Send null for empty reference fields (backend default for "not set")
    const accountDetails: any = {
      ...data.account_details,
      available_to_locations: data.account_details.available_to_locations || [],
      inv_reference_field: data.account_details.inv_reference_field || null,
      ref_reference_field: data.account_details.ref_reference_field || null,
      po_reference_field: data.account_details.po_reference_field || null,
      dept_reference_field: data.account_details.dept_reference_field || null,
    };

    const payload = {
      ...data,
      account_details: accountDetails,
    };
    validateAddress(payload);
  };

  const handleCheckboxClick = () => {
    if (isEulaAccepted) {
      // If already accepted, just uncheck
      methods.setValue("account_details.fedex_end_user_license_agreement", false);
    } else {
      // If not accepted, open the modal
      setIsEulaModalOpen(true);
    }
  };

  const handleEulaAccept = () => {
    methods.setValue("account_details.fedex_end_user_license_agreement", true);
    setIsEulaModalOpen(false);
  };

  const handleEulaDecline = () => {
    setIsEulaModalOpen(false);
  };

  const handleDiscard = () => {
    confirmNavigation(() => {
      navigate("/settings/parcel-accounts", { replace: true });
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="text-lg font-semibold text-primary">
          {intl.formatMessage({
            id: "settings.parcelAccounts.createAccount.title",
            defaultMessage: "Add new parcel account",
          })}
        </div>
        <div className="text-sm font-regular text-tertiary mb-xl">
          <FormattedMessage
            id="settings.parcelAccounts.createAccount.subtitle"
            defaultMessage="You have to register your account at the {fedex} website first."
            values={{ fedex: <a className="text-brand-tertiary">FedEx®</a> }}
          />{" "}
          <a className="text-brand-tertiary">
            {intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.subtitleHelpLink",
              defaultMessage: "Need help setting up Courier Service?",
            })}
          </a>
        </div>
        <div className="d-flex mb-2xl">
          <Radio
            isBordered
            options={[{ label: <FedexLogo />, value: "fedex" }]}
            value="fedex"
            size="md"
            onChange={() => {}}
          />
        </div>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div className="text-sm font-medium text-secondary-v2 mr-sm">
              {intl.formatMessage({
                id: "settings.parcelAccounts.createAccount.statusLabel",
                defaultMessage: "Status",
              })}
            </div>
            <Badge
              label={
                isActive
                  ? intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.statusActive",
                      defaultMessage: "Active",
                    })
                  : intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.statusInactive",
                      defaultMessage: "Inactive",
                    })
              }
              color={isActive ? "success" : "gray"}
              type="status-badge"
              size="md"
            />
          </div>
          <Controller
            name="account_details.is_active"
            control={control}
            render={({ field }) => <Switch id="custom-switch" checked={field.value} onChange={field.onChange} />}
          />
        </div>
        <div className="mt-2xl create-parcel-account__configuration-radio">
          <div className="text-sm font-medium text-secondary-v2 mb-sm">
            {intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.configurationLabel",
              defaultMessage: "Configuration",
            })}
          </div>
          <Controller
            name="account_details.account_type"
            control={control}
            render={({ field }) => (
              <Radio
                options={[
                  {
                    label: intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.configurationCompany",
                      defaultMessage: "Company",
                    }),
                    value: "company",
                  },
                  {
                    label: intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.configurationPaymentLocations",
                      defaultMessage: "Payment locations",
                    }),
                    value: "general",
                  },
                ]}
                value={field.value}
                size="md"
                onChange={field.onChange}
              />
            )}
          />
        </div>
        {accountType === "general" && (
          <div className="mt-sm create-parcel-account__dual-list">
            <DualListField
              required
              shouldUnregister
              name="account_details.available_to_locations"
              options={paymentLocations.map((location: any) => ({
                label: location.label,
                value: location.value,
              }))}
              optionsListLabel={intl.formatMessage({
                id: "settings.parcelAccounts.createAccount.availableLocation",
                defaultMessage: "Available location",
              })}
              selectedListLabel={intl.formatMessage({
                id: "settings.parcelAccounts.createAccount.selectedLocation",
                defaultMessage: "Selected location",
              })}
            />
          </div>
        )}
        <div className="mt-2xl">
          <Input
            label={intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.accountNameLabel",
              defaultMessage: "Account name",
            })}
            name="account_details.account_name"
            required
          />
        </div>
        <div className="mt-2xl">
          <Input
            label={intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.accountNumberLabel",
              defaultMessage: "Account number",
            })}
            name="account_details.account_number"
            required
          />
        </div>
        <div className="mt-2xl">
          <div className="text-sm font-medium text-primary mb-xs">
            {intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.addressLabel",
              defaultMessage: "Address",
            })}
          </div>
          <Alert
            label={intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.addressMatchingLabel",
              defaultMessage: "Address matching",
            })}
            variant="brand"
          >
            {intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.addressMatchingHint",
              defaultMessage: "Please ensure the entered address matches with the registered FedEx account",
            })}
          </Alert>
        </div>
        <Row className="mt-xl">
          <Col md={6}>
            <Controller
              name="country_code"
              rules={{ required: true }}
              control={control}
              render={({ field, fieldState }) => (
                <div>
                  <label className="cb-select__label required">
                    {intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.countryRegionLabel",
                      defaultMessage: "Country / Region",
                    })}
                  </label>
                  <Select
                    options={countries}
                    onChange={(option: any) => field.onChange(option ? option.value : "")}
                    value={countries.find((o: any) => o.value === field.value) || null}
                    classNamePrefix="cb-select"
                    classNames={{ option: () => "text-md" }}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  />
                  {fieldState.error && (
                    <div className="text-sm text-error mt-sm">
                      {intl.formatMessage({
                        id: "general.requiredField",
                        defaultMessage: "This is a required field.",
                      })}
                    </div>
                  )}
                </div>
              )}
            />
          </Col>
          <Col md={6}>
            <Input
              label={intl.formatMessage({
                id: "settings.parcelAccounts.createAccount.postalCodeLabel",
                defaultMessage: "Postal / Zip Code",
              })}
              name="postal_code"
              required
            />
          </Col>
        </Row>
        <Row className="mt-xl">
          <Col md={6}>
            <Input
              label={intl.formatMessage({
                id: "settings.parcelAccounts.createAccount.stateProvinceLabel",
                defaultMessage: "Province Code / State Code",
              })}
              name="state_code"
              registerOptions={{
                validate: {
                  maxLength: (v: string) =>
                    (v?.length || 0) <= 2 ||
                    intl.formatMessage({
                      id: "validation.provinceCodeMaxLength",
                      defaultMessage: "Province code must be 2 characters long",
                    }),
                },
              }}
            />
          </Col>
          <Col md={6}>
            <Input
              label={intl.formatMessage({
                id: "settings.parcelAccounts.createAccount.cityLabel",
                defaultMessage: "City",
              })}
              name="city"
              required
            />
          </Col>
        </Row>
        <Row className="mt-xl">
          <Col md={6}>
            <Input
              label={intl.formatMessage({
                id: "settings.parcelAccounts.createAccount.addressLine1Label",
                defaultMessage: "Address line 1",
              })}
              name="address_line_1"
              required
            />
          </Col>
          <Col md={6}>
            <Input
              label={intl.formatMessage({
                id: "settings.parcelAccounts.createAccount.addressLine2Label",
                defaultMessage: "Address line 2",
              })}
              name="address_line_2"
            />
          </Col>
        </Row>
        <div className="mt-2xl create-parcel-account__configuration-radio">
          <div className="text-sm font-medium text-secondary-v2 mb-sm required">
            {intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.labelFormatLabel",
              defaultMessage: "Create labels in",
            })}
          </div>
          <Controller
            name="account_details.label_format"
            control={control}
            render={({ field }) => (
              <Radio
                options={[
                  {
                    label: intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.labelFormatPdf",
                      defaultMessage: "PDF",
                    }),
                    value: "pdf",
                  },
                  {
                    label: intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.labelFormatZpl",
                      defaultMessage: "ZPL",
                    }),
                    value: "zpl",
                  },
                ]}
                value={field.value}
                size="md"
                onChange={field.onChange}
              />
            )}
          />
        </div>
        <div className="mt-2xl">
          <div className="text-sm font-medium text-primary mb-sm">
            {intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.labelReferencesLabel",
              defaultMessage: "Label References",
            })}
          </div>
        </div>
        <Row className="mt-xl">
          <Col md={6}>
            <Controller
              name="account_details.inv_reference_field"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="cb-select__label">
                    {intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.referenceLine1Label",
                      defaultMessage: "Reference - INV",
                    })}
                  </label>
                  <Select
                    placeholder={intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.referenceNotSet",
                      defaultMessage: "Not set yet",
                    })}
                    options={referenceOptions}
                    onChange={(option: any) => field.onChange(option ? option.value : null)}
                    value={referenceOptions.find((o) => o.value === field.value) || null}
                    classNamePrefix="cb-select"
                    classNames={{ option: () => "text-md" }}
                    isSearchable={false}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
              )}
            />
          </Col>
        </Row>
        <Row className="mt-xl">
          <Col md={6}>
            <Controller
              name="account_details.po_reference_field"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="cb-select__label">
                    {intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.referenceLine3Label",
                      defaultMessage: "Reference - PO",
                    })}
                  </label>
                  <Select
                    placeholder={intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.referenceNotSet",
                      defaultMessage: "Not set yet",
                    })}
                    options={referenceOptions}
                    onChange={(option: any) => field.onChange(option ? option.value : null)}
                    value={referenceOptions.find((o) => o.value === field.value) || null}
                    classNamePrefix="cb-select"
                    classNames={{ option: () => "text-md" }}
                    isSearchable={false}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
              )}
            />
          </Col>
        </Row>
        <Row className="mt-xl">
          <Col md={6}>
            <Controller
              name="account_details.dept_reference_field"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="cb-select__label">
                    {intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.referenceLine4Label",
                      defaultMessage: "Reference - DEPT",
                    })}
                  </label>
                  <Select
                    placeholder={intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.referenceNotSet",
                      defaultMessage: "Not set yet",
                    })}
                    options={referenceOptions}
                    onChange={(option: any) => field.onChange(option ? option.value : null)}
                    value={referenceOptions.find((o) => o.value === field.value) || null}
                    classNamePrefix="cb-select"
                    classNames={{ option: () => "text-md" }}
                    isSearchable={false}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
              )}
            />
          </Col>
        </Row>
        <Row className="mt-xl">
          <Col md={6}>
            <Controller
              name="account_details.ref_reference_field"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="cb-select__label">
                    {intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.referenceLine2Label",
                      defaultMessage: "Reference - REF",
                    })}
                  </label>
                  <Select
                    placeholder={intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.referenceNotSet",
                      defaultMessage: "Not set yet",
                    })}
                    options={referenceOptions}
                    onChange={(option: any) => field.onChange(option ? option.value : null)}
                    value={referenceOptions.find((o) => o.value === field.value) || null}
                    classNamePrefix="cb-select"
                    classNames={{ option: () => "text-md" }}
                    isSearchable={false}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
              )}
            />
          </Col>
        </Row>
        <div className="mt-2xl">
          <div className="text-sm font-medium text-secondary-v2 mb-sm required">
            {intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.eulaLabel",
              defaultMessage: "FedEx End User License Agreement",
            })}
          </div>
          <Checkbox
            size="sm"
            checked={isEulaAccepted}
            label={
              <FormattedMessage
                id="settings.parcelAccounts.createAccount.eulaCheckbox"
                defaultMessage="By checking it, you have agreed to {eula}"
                values={{
                  eula: (
                    <span className="text-brand-tertiary">
                      {intl.formatMessage({
                        id: "settings.parcelAccounts.createAccount.eulaName",
                        defaultMessage: "FedEx EULA",
                      })}
                    </span>
                  ),
                }}
              />
            }
            onChange={handleCheckboxClick}
          />
        </div>
        <div className="mt-2xl text-sm text-tertiary">
          {intl.formatMessage({
            id: "settings.parcelAccounts.createAccount.trademarkNote",
            defaultMessage: "FedEx service marks are owned by Federal Express Corporation and are used by permission",
          })}
        </div>
        <div className="mt-xl d-flex align-items-center justify-content-end">
          <Button
            variant="secondary-gray"
            label={intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.discard",
              defaultMessage: "Discard",
            })}
            size="md"
            type="button"
            className="mr-md"
            onClick={handleDiscard}
          />
          <Button
            variant="primary"
            label={
              isValidating
                ? intl.formatMessage({
                    id: "settings.parcelAccounts.createAccount.validating",
                    defaultMessage: "Validating...",
                  })
                : intl.formatMessage({
                    id: "settings.parcelAccounts.createAccount.next",
                    defaultMessage: "Next",
                  })
            }
            size="md"
            type="submit"
            disabled={!isEulaAccepted || isValidating || !isValid}
          />
        </div>
        <CreateParcelAccountAgreementModal
          show={isEulaModalOpen}
          onAccept={handleEulaAccept}
          onDecline={handleEulaDecline}
        />
        <UnsavedChangesModal />
      </form>
    </FormProvider>
  );
}

export default CreateParcelAccountAddressForm;
